import {
  AccountClient,
  ArcherApiClient,
  LocalStorageTokenStore,
  type AuthenticatedUser,
} from '@archer/shared';
import { create } from 'zustand';

interface AuthState {
  account: AccountClient | null;
  api: ArcherApiClient | null;
  user: AuthenticatedUser | null;
  loading: boolean;
  error: string | null;
  apiKey: string | null;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthenticatedUser>;
  register: (input: {
    email: string;
    password: string;
    firstName: string;
    lastName?: string;
  }) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
  setApiKey: (key: string | null) => void;
}

const API_BASE = import.meta.env.VITE_ARCHER_API_URL ?? '/api';
const ACCOUNT_BASE = import.meta.env.VITE_ACCOUNT_BASE_URL ?? 'https://account.omniapps.net';

const tokenStore = new LocalStorageTokenStore();

export const useAuth = create<AuthState>((set, get) => ({
  account: null,
  api: null,
  user: null,
  loading: true,
  error: null,
  apiKey: localStorage.getItem('archer.ai.apiKey'),

  async init() {
    const account = new AccountClient(tokenStore, { baseUrl: ACCOUNT_BASE });
    const api = new ArcherApiClient(API_BASE, async () => {
      const token = await account.getValidAccessToken();
      return token ? { Authorization: `Bearer ${token}` } : ({} as Record<string, string>);
    });
    set({ account, api });
    const tokens = account.getTokens();
    if (tokens) {
      try {
        const user = await account.me();
        set({ user, loading: false });
        return;
      } catch {
        await account.logout().catch(() => undefined);
      }
    }
    set({ loading: false });
  },

  async login(email, password) {
    const account = get().account;
    if (!account) throw new Error('auth not initialised');
    set({ loading: true, error: null });
    try {
      const user = await account.login(email, password);
      set({ user, loading: false });
      return user;
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
      throw err;
    }
  },

  async register(input) {
    const account = get().account;
    if (!account) throw new Error('auth not initialised');
    set({ loading: true, error: null });
    try {
      const user = await account.register(input);
      set({ user, loading: false });
      return user;
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
      throw err;
    }
  },

  async logout() {
    const account = get().account;
    if (account) await account.logout().catch(() => undefined);
    set({ user: null });
  },

  setApiKey(key) {
    if (key) localStorage.setItem('archer.ai.apiKey', key);
    else localStorage.removeItem('archer.ai.apiKey');
    set({ apiKey: key });
  },
}));
